/**
 * V70 엔글 마블 — 디자인 토큰 (NGLE 브랜드 Teal 재디자인, 2026-10-19).
 *
 * 사용자 지시: 공식 홈페이지(www.ngle.co.kr) + 로고 에셋 브랜드 컬러로 밝고 세련되게.
 *
 * 공식 브랜드 팔레트 (교차 검증 — logo PNG 지배색 + ngle.co.kr/css/common.css):
 *   #00A6B5  Primary Teal (브랜드 포인트, 공식 CSS)
 *   #106070  Deep Teal (로고 어두운 영역)
 *   #9CDDE2  Light Teal (공식 CSS 보조)
 *   #C8E6E6  Pastel Teal (공식 CSS, 더 밝음)
 *   #DFF2F4  Mist Teal (파생 파스텔 — 배경)
 *   #222324  Dark Text (공식 CSS 메인)
 *   #65686B  Medium Text
 *   #DCDEE0  Light Border
 *   #FAFAFA  Soft White
 *   #F5C842  Accent Gold (START/보상)
 *   #F5883A  Accent Orange (경고/위험 신호 유지)
 *
 * 디자인 방향:
 *   - 어두운 네이비 톤 전면 교체 → 밝은 teal/화이트 베이스
 *   - 보드 24칸: 흰색/파스텔 teal 베이스 + Primary Teal 보더로 통일감
 *   - 특수칸만 명확 구분 — Gold(START), Primary Teal(이벤트), Light Teal(상점/워프)
 *   - 과거 무지개색 산만함 X, 어두운 톤 칙칙함 X
 *
 * 호환성: 기존 export shape (COLORS/RADIUS/SPACING) 유지 → import 지점 미변경.
 */

export const COLORS = {
  // 베이스 (밝은 Teal 변주 — 어두운 네이비 전면 교체)
  bgDeep: '#DFF2F4',          // 메인 배경 (Mist Teal, 가장 밝은 파스텔)
  bgCard: '#FFFFFF',          // 카드/패널 배경 (화이트)
  bgCell: '#F5FBFC',          // 셀 바탕 (더 밝은 파스텔)
  bgCellSoft: '#E8F5F7',      // 셀 보조 (파스텔 teal)
  bgElevated: '#C8E6E6',      // 입체 요소 (라이트 teal)

  // 액센트 — 브랜드 Teal + 보조 (기존 export 키 유지)
  cyan: '#00A6B5',            // ⭐ Primary Brand Teal
  cyanDeep: '#106070',        // ⭐ Deep Brand Teal
  gold: '#F5C842',            // ⭐ Accent Gold (START/보상)
  goldDeep: '#C99C2A',
  violet: '#9CDDE2',          // Light Teal (브랜드 보조 — 바이올렛 자리 재사용)
  violetDeep: '#5EB4BA',
  magenta: '#F5883A',         // Orange (강조/위험 — 마젠타 자리 재사용)
  magentaDeep: '#C96A1F',

  // 상태 (톤 다운, 원색 제거)
  success: '#00A6B5',         // 성공 — 브랜드 Teal
  successSoft: '#9CDDE2',
  danger: '#E04040',          // 위험 (공식 CSS 와 통일)
  dangerDeep: '#B82A2A',
  neutral: '#65686B',         // 중간 그레이 (공식 CSS)

  // 글씨 (공식 CSS 값 그대로)
  textPrimary: '#222324',     // 메인 텍스트 (공식 CSS)
  textSecondary: '#65686B',   // 보조 텍스트 (공식 CSS)
  textMuted: '#878B8E',       // 흐린 텍스트 (공식 CSS)
  textOnLight: '#222324',     // 밝은 배경 위 어두운 텍스트
  textOnLightMuted: '#65686B',

  // 특별 모서리 전경 — 밝은 배경에 어두운 글씨 (대비 WCAG AA)
  darkOnStart: '#5C4A1A',     // START 셀 (gold 바탕) 위 다크 brown-gold
  darkOnStartSoft: '#8C7A4A',
  darkOnTrap: '#106070',      // 보물칸 (teal 바탕) 위 Deep Teal
  darkOnTrapSoft: '#1A8090',
  darkOnVault: '#106070',
  darkOnVaultSoft: '#1A8090',

  // 셀별 투톤 배경 — NGLE 브랜드 Teal 변주로 통일.
  //   from(살짝 어두움) → to(살짝 밝음) gradient 유지 (레이아웃 변경 X).
  //   특수칸만 명확 구분: START=Gold, 보물/금고=Primary Teal, 상점/워프=Light Teal, 이벤트=화이트/파스텔.
  cell: {
    // 시작/도착 — Gold (명확한 강조)
    startFrom: '#F5C842', startTo: '#FFE07A',
    // 무인도/금고 — Primary Teal (브랜드 포인트, 보상)
    trapFrom: '#00A6B5', trapTo: '#2BBECC',
    vaultFrom: '#00A6B5', vaultTo: '#2BBECC',
    // 상점 — Light Teal (중립 기능)
    shopFrom: '#9CDDE2', shopTo: '#B8E8EC',
    // 이벤트 셀 (E1~E10) — 통일된 화이트/파스텔 베이스. 특수성은 아이콘/라벨 중심.
    //   파스텔 teal 2종 + 라이트 teal 2종 + 화이트/mist 2종 + accent teal 2종 로 variation.
    e1From: '#E8F5F7', e1To: '#F5FBFC',       // E1 Mist (문 선택)
    e2From: '#9CDDE2', e2To: '#B8E8EC',       // E2 Light Teal (가위바위보)
    e3From: '#00A6B5', e3To: '#2BBECC',       // E3 Primary Teal (보물상자)
    e4From: '#C8E6E6', e4To: '#DFF2F4',       // E4 Pastel (동전)
    e5From: '#F5FBFC', e5To: '#FFFFFF',       // E5 Lightest (폭탄)
    e6From: '#9CDDE2', e6To: '#B8E8EC',       // E6 Light Teal (운명 카드)
    e7From: '#C8E6E6', e7To: '#DFF2F4',       // E7 Pastel (홀짝)
    e8From: '#00A6B5', e8To: '#2BBECC',       // E8 Primary Teal (방패)
    e9From: '#9CDDE2', e9To: '#B8E8EC',       // E9 Light Teal (워프)
    e10From: '#F5C842', e10To: '#FFE07A',     // E10 Gold (잭팟 — 보상 액센트)
  },

  // 그림자 — 네온 glow 제거, soft shadow (브랜드 teal 톤 살짝)
  shadowSoft: '0 1px 3px rgba(16, 96, 112, 0.1)',
  shadowCell: '0 2px 6px rgba(16, 96, 112, 0.15), inset 0 1px 0 rgba(255,255,255,0.6)',
  //   활성 셀 outline: Primary Teal 2px + 미세 teal soft shadow
  shadowCellActive: '0 0 0 2px #00A6B5, 0 2px 8px rgba(0, 166, 181, 0.3), inset 0 1px 0 rgba(255,255,255,0.4)',
  shadowDice: '0 3px 10px rgba(16, 96, 112, 0.2), inset 0 1px 2px rgba(255,255,255,0.6)',
  shadowBtn: '0 2px 6px rgba(0, 166, 181, 0.25)',
} as const;

export const RADIUS = {
  sm: 4, md: 6, lg: 8, xl: 12, pill: 999,
} as const;

export const SPACING = {
  xs: 2, sm: 4, md: 8, lg: 12, xl: 16, xxl: 24,
} as const;
